// Mirrors apps/evertel-rn/src/ErrorHandler.ts
// Intercepts console.error and console.warn to POST errors to the API in production.
// Avoiding DI for root ErrorHandler — imports apiWebInstance directly.

import { apiWebInstance as api } from './api/apiWebInstance'

const state = {
    additionalInfo: null as any
}

/**
 * Use this to add additional logged-in user / session / device
 * information after app is initialized. Called from App component
 * whenever currentUserId or isInitialized changes.
 */
export function setAdditionalErrorInfo(info: any) {
    state.additionalInfo = info
}

export function setupErrorHandler() {
    const originalConsoleWarn = console.warn
    const originalConsoleError = console.error

    // Disable console.log in production (match mobile)
    if (api.isProduction) console.log = () => { /** disable logging in prod */ }

    const suppressMessages = ['Authorization Required', 'Network Error']

    const isLocalhost = window.location.hostname === 'localhost'

    const shouldSuppressMessage = (args: any[]) => {
        return args.some(arg =>
            typeof arg === 'string' && suppressMessages.some(sm => arg.includes(sm))
        )
    }

    const formatErrorObject = (error: any) => {
        let msg: string
        if (error instanceof Error) {
            msg = `Name: ${error.name}\nMessage: ${error.message}\n\nStack: \n${error.stack || ''}`
        } else {
            msg = String(error)
        }
        if (error && typeof error === 'object') {
            if ('url' in error) {
                msg += `\nUrl: ${error.url}`
                msg += `\nStatus: ${error.status}`
                msg += `\nCode: ${error.code}`
            }
        }
        return msg
    }

    const formatLog = (prefix: string, args: any[]) => {
        if (args.length === 0) return ''

        const formattedArgs = args.map(arg => {
            if (arg instanceof Error) {
                return formatErrorObject(arg)
            }
            if (typeof arg === 'string') {
                return arg
            }
            return JSON.stringify(arg, null, 2)
        })

        return `${prefix}: ${formattedArgs.join('\n----\n')}`
    }

    console.warn = (...args: any[]) => {
        if (shouldSuppressMessage(args)) return

        originalConsoleWarn.call(console, ...args)

        if (isLocalhost) return

        const log = formatLog('EHWarning', args)
        if (log) {
            api.Routes.App.postLog({ msg: log, data: state.additionalInfo || {} }).catch(e => {
                originalConsoleWarn.call(console, 'API error in console.warn:', { incepted: e, original: log })
            })
        }
    }

    console.error = (...args: any[]) => {
        if (shouldSuppressMessage(args)) return

        originalConsoleError.call(console, ...args)

        if (isLocalhost) return

        const log = formatLog('EHError', args)
        if (log) {
            api.Routes.App.postLogError({ msg: log, data: state.additionalInfo || {} }).catch(e => {
                originalConsoleError.call(console, 'API error in console.error:', { incepted: e, original: log })
            })
        }
    }
}
